import React, { Component, setGlobal, Fragment } from "reactn";
import styled from "styled-components";
import {
    Dialog,
    Button,
    IconButton,
    MenuItem,
    Icon,
    Select,
    DialogTitle,
    TextField,
    DialogActions,
} from "@material-ui/core";

import { FlexBox } from "js/react/components/LayoutGrid";
import {
    BeautifulDialog,
    DialogContent, ShowConfirmationDialog,
    ShowDialog,
    ShowErrorDialog,
    ShowMessageDialog,
    ShowUpgradeDialog
} from "js/react/components/Dialogs/BaseDialog";
import { FlexSpacer, Gap10, Gap20 } from "js/react/components/Gap";
import {
    flattenPresentationLinkEmails,
    flattenPermissions,
    InviteInputWithGContacts
} from "js/react/components/EmailInput/InviteInput";
import { BlueButton } from "js/react/components/UiComponents";
import ProBadge from "js/react/components/ProBadge";
import { themeColors } from "js/react/sharedStyles";
import { PresentationPrivacyType } from "js/core/models/presentation";
import { app } from "js/namespaces";
import { FeatureType } from "js/core/models/features";
import { trackActivity } from "js/core/utilities/utilities";
import shouldShowProBadge from "js/editor/shouldShowProBadge";
import linkDS, { LinkType } from "js/react/views/PresentationSettings/dataservices/LinkDataService";
import { incUserProps } from "js/analytics";
import { UIController } from "js/editor/dialogs/UIController";
import Api from "js/core/api";
import Loadable from "js/react/components/Loadable";
import { ds } from "js/core/models/dataService";
import { getPricingPageUrl } from "js/core/utilities/pricing";
import { _ } from "../../../../vendor";
import permissionsDS from "../dataservices/PermissionsDataService";
import log from "../../../../core/logger";
import TextArea from "js/react/components/TextArea";
import { UpgradePlanDialogType } from "../../MarketingDialogs/UpgradePlanDialog";
import PresentationSettingsContainer from "../PresentationSettingsContainer";
import PermissionsList from "../panes/PermissionsList";
import { WarningWatermark } from "js/react/components/WarningWatermark";
import { EMAIL_VALIDATION_REGEX } from "js/react/constants";
import LinkExpiration from "js/react/components/LinkExpiration";
import { ToggleAllowCommenting } from "../components/ToggleAllowCommenting";

const ShareDialogContainer = styled(Dialog)`
  .MuiPaper-root {
    display: grid;
    row-gap: 10px;
    grid-auto-flow: row;
    background-color: transparent;
  }
  
  .MuiOutlinedInput-root.Mui-disabled {
    height: 44px;
    pointer-events: none;
  }
  
  .MuiDialogTitle-root{
    padding: 24px 34px 20px 34px;
    .MuiIcon-root{
        font-size: 34px;
        color: #11a9ef;
        vertical-align: -9px;
        margin-right: 10px;
    }
  }
  
  .dialog-content {
    padding-bottom: 14px;
  }
`;

const DialogPanel = styled.div`
  background: white;
  border-radius: 4px;
  padding: 0px;
  max-width: min(100vw - 20px, 860px);
`;

const Expander = styled.div`
  transition: 300ms;
  overflow: hidden;
  height: auto;
`;

export class ShareDialog extends Component {
    constructor(props) {
        super(props);

        this.state = {
            selectedPanel: props.selectedPanel || "link",
        };
    }

    handleSetSelected = selectedPanel => {
        this.setState({
            selectedPanel,
        });
    }

    render() {
        let { selectedPanel } = this.state;

        return (
            <ShareDialogContainer
                open
                className="share-dialog-container"
                maxWidth="md"
                onEscapeKeyDown={event => {
                    event.stopPropagation();
                    this.props.closeDialog();
                }}
                onBackdropClick={() => {
                    this.props.closeDialog();
                }}
            >
                {
                    app.isConstrained &&
                    <FlexBox right>
                        <IconButton className="exit-button" onClick={this.props.closeDialog}>
                            <Icon>close</Icon>
                        </IconButton>
                    </FlexBox>
                }
                <CollaboratePanel
                    emails={this.props.emails}
                    location={this.props.location || "simple-share"}
                    presentation={this.props.presentation}
                    expanded={selectedPanel == "collaborate"}
                    onSelected={() => this.handleSetSelected("collaborate")}
                    closeDialog={this.props.closeDialog}
                />
                <ShareLinkPanel
                    presentation={this.props.presentation}
                    expanded={selectedPanel == "link"}
                    onSelected={() => this.handleSetSelected("link")}
                    closeDialog={this.props.closeDialog}
                />
                <SecuredInvitePanel
                    presentation={this.props.presentation}
                    expanded={selectedPanel == "invite"}
                    onSelected={() => this.handleSetSelected("invite")}
                    closeDialog={this.props.closeDialog}
                />
            </ShareDialogContainer>
        );
    }
}

class ShareLinkPanel extends Component {
    state = {
        link: null
    }

    constructor() {
        super();
        this.linkRef = React.createRef();
    }

    componentDidMount() {
        if (!this.props.presentation.links) {
            this.props.presentation.loadPresentationLinks();
        }

        let link = this.props.presentation.links.find(link => link.get("isDefault") && link.get("type") == "public");

        if (link) {
            this.setState({
                link,
                linkExpiresAt: link.get("expiresAt")
            });
        } else {
            linkDS.createPublicLink(this.props.presentation)
                .then(link => {
                    link.update({ isDefault: true });
                    this.setState({ link: link, shouldCleanup: true });
                })
                .catch(err => {
                    console.error(err);
                });
        }
    }

    componentWillUnmount() {
        if (!this.state.linkCopied && this.state.link && this.state.shouldCleanup) {
            this.state.link.destroy();
        }
    }

    handleAccept = async () => {
        let { presentation } = this.props;
        let { link } = this.state;

        if (!link.get("enabled")) {
            link.update({ enabled: true });
        }

        // set the presentation to PUBLIC if necessary
        if (presentation.getPrivacySetting() !== PresentationPrivacyType.PUBLIC) {
            if (!(await ShowConfirmationDialog({
                title: "Make this presentation public?",
                message: <>
                    <p>In order to share your presentation, its privacy setting must be set to <b>Public.</b></p>
                    <br/>
                    <p>Public presentations can only be viewed by people who have been given a link or invited to the presentation.</p>
                </>
            }))) {
                return;
            }

            presentation.setPrivacySetting(PresentationPrivacyType.PUBLIC);

            const eventProps = {
                presentation_id: presentation.get("id"),
                old_value: presentation.getPrivacySetting(),
                new_value: PresentationPrivacyType.PUBLIC
            };
            trackActivity("Sharing", "ChangePrivacySetting", null, null, eventProps, true);
        }

        await linkDS.saveLink(presentation, link);
        this.setState({ linkCopied: true });

        const shareSuccessful = () => {
            this.props.closeDialog();
            const props = {
                presentation_id: presentation.id,
                link_ids: [link.get("id")],
                is_new: true,
                type: "public_link",
                workspace_id: presentation.getWorkspaceId()
            };
            trackActivity("Sharing", "CopyLink", null, null, props, { audit: true });
        };

        // Try to share. If it's cancelled or unsupported, we do not continue tracking
        try {
            if (app.isMobileOrTablet && window.navigator.share) {
                await window.navigator.share({
                    url: link.getLinkURL(),
                });
                shareSuccessful();
            } else
            if (window.navigator.clipboard) {
                await window.navigator.clipboard.writeText(
                    link.getLinkURL()
                );
                shareSuccessful();
            }
        } catch (error) {
            console.log("[Share Canceled]", error);
        }
    };

    toggleAllowCommenting = event => {
        let { link } = this.state;
        if (link) {
            const allowCommenting = !link.attributes.allowCommenting;
            link.update({ allowCommenting });
        }
    }

    handleUpdateLinkExpiration = ({ linkShouldExpire, linkHasExpired, linkExpiresAt }) => {
        this.setState({
            linkShouldExpire,
            linkHasExpired,
            linkExpiresAt
        });

        if (!linkHasExpired) {
            this.state.link.update({
                expiresAt: linkShouldExpire ? linkExpiresAt : null
            });
        }
    }

    handleShowMoreOptions = () => {
        this.props.closeDialog();
        const { presentation } = this.props;
        setGlobal({
            presentation: presentation,
            presentationId: presentation.id,
        });
        ShowDialog(PresentationSettingsContainer, { startPane: "share" });
    }

    render() {
        let { expanded, presentation } = this.props;
        let { link, linkExpiresAt, linkHasExpired } = this.state;

        const useShareIcon = app.isMobileOrTablet && window.navigator.share;
        const disabled = !link || linkHasExpired || (link?.shouldLinkExpire() && link?.isLinkExpired());

        const allowCommenting = link?.attributes.allowCommenting;

        return (
            <DialogPanel onClick={this.props.onSelected}>
                <DialogTitle><Icon>link</Icon>Share Public Link</DialogTitle>
                <Expander style={{ maxHeight: expanded ? "500px" : "0px" }}>
                    <DialogContent>
                        <h3>
                            Anyone can use this link to view this presentation.
                        </h3>
                        {
                            !app.isConstrained &&
                            <Gap20/>
                        }
                        <FlexBox>
                            {
                                !app.isConstrained &&
                                <Fragment>
                                    <FlexSpacer>
                                        <TextField
                                            inputRef={this.linkRef}
                                            disabled
                                            value={link ? link.getLinkURL() : "Generating link..."}
                                            fullWidth
                                            variant="outlined"
                                        />
                                    </FlexSpacer>
                                    <Gap10/>
                                </Fragment>
                            }
                            {
                                !useShareIcon &&
                                <BlueButton onClick={this.handleAccept} disabled={disabled}>
                                    Copy Link
                                </BlueButton>
                            }
                            {
                                useShareIcon &&
                                <BlueButton onClick={this.handleAccept} disabled={disabled}>
                                    <Icon>ios_share</Icon>
                                </BlueButton>
                            }
                        </FlexBox>

                        <Gap20 />

                        <ToggleAllowCommenting
                            allowCommenting={allowCommenting}
                            onToggle={this.toggleAllowCommenting}
                        />

                        <LinkExpiration
                            expiresAt={linkExpiresAt}
                            presentation={presentation}
                            onUpdate={this.handleUpdateLinkExpiration}
                        />

                    </DialogContent>
                    <WarningWatermark
                        cta="GetLinkWarningWatermark"
                        presentation={this.props.presentation}
                    />
                    {
                        !app.isConstrained && app.mainView.editorView &&
                        <DialogActions>
                            <Button onClick={this.handleShowMoreOptions}>More Sharing Options...</Button>
                            <FlexSpacer/>
                        </DialogActions>
                    }
                </Expander>
            </DialogPanel>
        );
    }
}

export class SecuredInvitePanel extends Component {
    state = {
        customMessage: "",
        emails: [],
        isInviteDisabled: true,
        hasLinkExpiration: false,
        allowCommenting: false,
    };

    get disableSendEmail() {
        const { hasLinkAlreadyExpired, hasLinkExpiration } = this;
        const { isInviteDisabled } = this.state;
        return isInviteDisabled ||
            !app.user.features.isFeatureEnabled(FeatureType.SECURED_SHARING, UIController.getWorkspaceId()) ||
            (hasLinkExpiration && hasLinkAlreadyExpired);
    }

    handlePreviewEmail = async () => {
        ShowDialog(InvitePreviewEmailDialog, {
            presentation: this.props.presentation,
            customMessage: this.state.customMessage
        });
    };

    handleChangeCustomMessage = customMessage => {
        this.setState({
            customMessage
        });
    };

    handleInputChange = (emails, isInviteDisabled) => {
        this.setState({ emails, isInviteDisabled });
    }

    handleDuplicatedEmails = (flattenedEmailsObj, duplicatedEmails) => {
        const { emails } = this.state;
        this.setState({
            emails: emails.filter(tag => (tag.email ? !flattenedEmailsObj[tag.email] : !flattenedEmailsObj[tag])),
        });

        const errorMessage = "The following users already have access to this presentation: ";
        ShowErrorDialog({
            error: "Oops!",
            message: errorMessage.concat(duplicatedEmails.join(", "))
        });
    };

    handleAccept = async () => {
        const presentation = this.props.presentation;

        if (!presentation.get("secured") && presentation.get("public")) {
            if (!(await ShowConfirmationDialog({
                title: "Your presentation is currently public",
                message: <Fragment>
                    <p>In order to send a secured invite, your presentation's security setting will be set to <b>Secured.</b></p>
                    <p>Any existing public links or embeds for this presentation will be disabled.</p>
                    <br/>
                    <b>Are you sure you wish to continue?</b>
                </Fragment>
            }))) {
                return;
            }
        }

        const {
            emails,
            customMessage,
            linkExpiresAt,
            allowCommenting,
        } = this.state;
        const lowercaseEmails = emails.map(email => email.email || email).map(email => email.toLowerCase());

        const links = _.filter(presentation.links.models, link => link.get("type") == LinkType.INVITE);
        const flattenedEmailsObj = flattenPresentationLinkEmails(links);
        const duplicatedEmails = lowercaseEmails.filter(email => flattenedEmailsObj[email]);
        if (duplicatedEmails.length) {
            this.handleDuplicatedEmails(flattenedEmailsObj, duplicatedEmails);
            return;
        }

        try {
            const linkIds = [];

            for (let email of emails) {
                const link = await linkDS.createInviteLink(
                    presentation,
                    email,
                    customMessage,
                    linkExpiresAt,
                    allowCommenting
                );
                await linkDS.saveLink(presentation, link);
                linkIds.push(link.id);
            }

            const props = {
                presentation_id: presentation.id,
                link_ids: linkIds,
                is_new: true,
                type: "email",
                sender_email: app.user.getEmail(),
                recipient_emails: emails,
                workspace_id: presentation.getWorkspaceId()
            };
            trackActivity("Sharing", "EmailLink", null, null, props, { audit: true });

            if (!presentation.get("secured")) {
                await presentation.setPrivacySetting(PresentationPrivacyType.SECURED);
                ShowMessageDialog({
                    title: "Secured invites were sent successfully!",
                    message: "Your presentation's security setting has been set to Secured."
                });
            } else {
                ShowMessageDialog({
                    title: "Secured invites were sent successfully!",
                });
            }
        } catch (err) {
            ShowErrorDialog({
                error: "Error while sharing",
                message: err.message
            });
            console.error(err);
        } finally {
            this.props.closeDialog();
        }
    };

    toggleAllowCommenting = event => {
        const allowCommenting = !this.state.allowCommenting;
        this.setState({ allowCommenting });
    }

    onUpdateLinkExpiration = linkExpiration => {
        this.setState(linkExpiration);
    }

    render() {
        let { expanded, presentation } = this.props;
        const { disableSendEmail } = this;
        const {
            customMessage,
            emails,
            linkExpiresAt,
            linkShouldExpire,
            linkHasExpired,
            allowCommenting,
        } = this.state;
        const disabled = disableSendEmail || (linkShouldExpire && linkHasExpired);

        return (
            <DialogPanel onClick={this.props.onSelected}>
                <DialogTitle>
                    <FlexBox left middle>
                        <Icon>lock</Icon>
                        Share Securely
                        <ProBadge
                            show={shouldShowProBadge(FeatureType.SECURED_SHARING, UIController.getWorkspaceId())}
                            analytics={{ cta: "SecuredSharing", ...this.props.presentation.getAnalytics() }}
                            workspaceId={UIController.getWorkspaceId()}
                        />
                    </FlexBox>
                </DialogTitle>
                <Expander style={{ maxHeight: expanded ? "600px" : "0px" }}>
                    {
                        !app.isConstrained &&
                        <Fragment>
                            <DialogContent>
                                <h3>
                                    We'll send an email to people you invite to view this presentation. The recipients will need
                                    to verify their identity and you'll be able to track analytics whether they've viewed your
                                    presentation and how long they've spent on each slide.
                                </h3>
                                <Gap20/>
                                <InviteInputWithGContacts
                                    onInputChange={this.handleInputChange}
                                    tags={emails}
                                    showGoogleContacts={false}
                                    inputProps={{
                                        readOnly: shouldShowProBadge(FeatureType.SECURED_SHARING, UIController.getWorkspaceId()),
                                        onClick: () => {
                                            if (shouldShowProBadge(FeatureType.SECURED_SHARING, UIController.getWorkspaceId())) {
                                                ShowUpgradeDialog({
                                                    type: UpgradePlanDialogType.UPGRADE_PLAN,
                                                    analytics: { cta: "SecuredSharing", ...this.props.presentation.getAnalytics() },
                                                    workspaceId: UIController.getWorkspaceId()
                                                });
                                            }
                                        }
                                    }}
                                />
                                <Gap10/>
                                <TextArea
                                    readOnly={shouldShowProBadge(FeatureType.SECURED_SHARING, UIController.getWorkspaceId())}
                                    isVisible
                                    text={customMessage}
                                    onChange={customMessage => this.handleChangeCustomMessage(customMessage) }
                                    placeholder="Add a note..."
                                    inputProps={{
                                        onClick: () => {
                                            if (shouldShowProBadge(FeatureType.SECURED_SHARING, UIController.getWorkspaceId())) {
                                                ShowUpgradeDialog({
                                                    type: UpgradePlanDialogType.UPGRADE_PLAN,
                                                    analytics: { cta: "SecuredSharing", ...this.props.presentation.getAnalytics() },
                                                    workspaceId: UIController.getWorkspaceId()
                                                });
                                            }
                                        }
                                    }}
                                />
                                <Gap20 />
                                <ToggleAllowCommenting
                                    allowCommenting={allowCommenting}
                                    onToggle={this.toggleAllowCommenting}
                                />

                                <LinkExpiration
                                    expiresAt={linkExpiresAt}
                                    presentation={presentation}
                                    onUpdate={this.onUpdateLinkExpiration}
                                />

                            </DialogContent>
                            <DialogActions>
                                <Button onClick={this.handlePreviewEmail}>Preview Email</Button>
                                <FlexSpacer/>
                                <BlueButton disabled={disabled} onClick={this.handleAccept} color="primary">
                                    Send Email
                                </BlueButton>
                            </DialogActions>
                        </Fragment>
                    }
                    {
                        app.isConstrained &&
                        <Fragment>
                            <DialogContent>
                                <InviteInputWithGContacts
                                    onInputChange={this.handleInputChange}
                                    tags={emails}
                                    showGoogleContacts={false}
                                    inputProps={{
                                        readOnly: shouldShowProBadge(FeatureType.SECURED_SHARING, UIController.getWorkspaceId()),
                                        onClick: () => {
                                            if (shouldShowProBadge(FeatureType.SECURED_SHARING, UIController.getWorkspaceId())) {
                                                ShowUpgradeDialog({
                                                    type: UpgradePlanDialogType.UPGRADE_PLAN,
                                                    analytics: { cta: "SecuredSharing", ...this.props.presentation.getAnalytics() },
                                                    workspaceId: UIController.getWorkspaceId()
                                                });
                                            }
                                        }
                                    }}
                                />
                                <Gap10/>
                                <TextArea
                                    readOnly={shouldShowProBadge(FeatureType.SECURED_SHARING, UIController.getWorkspaceId())}
                                    isVisible
                                    text={customMessage}
                                    onChange={customMessage => this.handleChangeCustomMessage(customMessage) }
                                    placeholder="Add a note..."
                                    inputProps={{
                                        onClick: () => {
                                            if (shouldShowProBadge(FeatureType.SECURED_SHARING, UIController.getWorkspaceId())) {
                                                ShowUpgradeDialog({
                                                    type: UpgradePlanDialogType.UPGRADE_PLAN,
                                                    analytics: { cta: "SecuredSharing", ...this.props.presentation.getAnalytics() },
                                                    workspaceId: UIController.getWorkspaceId()
                                                });
                                            }
                                        }
                                    }}
                                />
                                <Gap20/>
                                <LinkExpiration presentation={presentation} onUpdate={this.onUpdateLinkExpiration} />

                                <Gap20/>
                                <h3 style={{ margin: 0 }}>
                                    Recipients will need to verify their identity to view this presentation.
                                </h3>
                            </DialogContent>
                            <DialogActions
                                style={{
                                    flexDirection: "column",
                                    justifyContent: "center",
                                }}
                            >
                                <BlueButton disabled={disableSendEmail} onClick={this.handleAccept} color="primary">
                                    Send Email
                                </BlueButton>
                                <Gap20/>
                                <Button
                                    className="text-blue"
                                    onClick={this.handlePreviewEmail}
                                >Preview Email</Button>
                            </DialogActions>
                        </Fragment>
                    }
                </Expander>
            </DialogPanel>
        );
    }
}

export class InvitePreviewEmailDialog extends Component {
    state = {
        isLoading: true
    }

    async componentDidMount() {
        const response = await Api.renderPresentationLinkEmail.post({
            presentationId: this.props.presentation.id,
            customMessage: this.props.customMessage
        });

        this.setState({
            isLoading: false,
            previewHTML: response.html
        });
    }

    render() {
        let { isLoading, previewHTML } = this.state;

        return (
            <BeautifulDialog maxWidth="lg" fullWidth={false} closeButton={app.isConstrained} closeDialog={this.props.closeDialog}>
                <Loadable isLoading={isLoading}>
                    <DialogContent
                        dangerouslySetInnerHTML={{ __html: previewHTML }}
                    ></DialogContent>
                </Loadable>
            </BeautifulDialog>
        );
    }
}

const TeamGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  max-height: 115px;
  overflow-y: auto;
  button {
    height: 32px;
    margin-right: 10px;
    margin-bottom: 10px;
  }
`;

const TeamCTA = styled.div`
  font-size: 15px;
  font-weight: 600;
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  a {
    color: ${themeColors.ui_blue} ;
    cursor: pointer;
  } 
`;

class CollaboratePanel extends Component {
    constructor(props) {
        super(props);

        // Reduce the emails provided by props to valid emails
        let validEmails = [];
        (props.emails || []).forEach(email => {
            email.match(EMAIL_VALIDATION_REGEX) && validEmails.push(email);
        });

        this.state = {
            customMessage: "",
            emails: validEmails,
            isInviteDisabled: true,
            permission: "write",
            isLoading: false,
            showCollaboratorList: false,
        };
    }

    handleToggleCollaboratorList = showCollaboratorList => {
        const { presentation } = this.props;
        setGlobal({
            presentation: presentation,
            presentationId: presentation.id,
        });
        this.setState({
            showCollaboratorList: showCollaboratorList,
        });
    }

    handleViewAllCollaborators = () => {
        this.props.closeDialog();
        const { presentation } = this.props;
        setGlobal({
            presentation: presentation,
            presentationId: presentation.id,
        });
        ShowDialog(PresentationSettingsContainer, { startPane: "collaborate" });
    }

    handleTeamCTA = () => {
        const userIsBasicInWorkspace = app.user.features.isFeatureEnabled(FeatureType.UPGRADE, UIController.getWorkspaceId());
        const currentPlan = userIsBasicInWorkspace ? "basic" : app.user.analyticsPersonalPlan;
        window.open(getPricingPageUrl(app && app.user.hasTakenTrial, app && app.user.has("hasTakenTeamTrial"), currentPlan), "_blank");
    }

    handleChangeCustomMessage = customMessage => {
        this.setState({
            customMessage
        });
    }

    handleInputChange = (emails, isInviteDisabled) => {
        this.setState({ emails, isInviteDisabled });
    }

    handleDropdownChange = value => {
        this.setState({ permission: value });
    }

    handleDuplicatedEmails = (flattenedEmailsObj, duplicatedEmails) => {
        const { emails } = this.state;
        this.setState({
            emails: emails.filter(tag => (tag.email ? !flattenedEmailsObj[tag.email] : !flattenedEmailsObj[tag])),
            isLoading: false
        });

        const errorMessage = "The following users already have access to this presentation: ";
        ShowErrorDialog({
            error: "Oops!",
            message: errorMessage.concat(duplicatedEmails.join(", "))
        });
    };

    handleAccept = async () => {
        const { emails, permission, customMessage } = this.state;
        const { location } = this.props;
        this.setState({ isLoading: true });
        try {
            const results = await Api.permissions.get({ id: this.props.presentation.id });
            const presentationPermissions = results.permissions;

            const permissions = _.filter(
                presentationPermissions,
                perm => perm.invite == false || perm.invite == undefined
            );
            const flattenedEmailsObj = flattenPermissions(permissions);
            const duplicatedEmails = emails.filter(email => flattenedEmailsObj[email]);
            if (duplicatedEmails.length) {
                this.handleDuplicatedEmails(flattenedEmailsObj, duplicatedEmails);
                return;
            }

            const payload = { emails, permission, customMessage };

            this.setState({ emails: [], permission: "write", customMessage: "", isInviteDisabled: true });

            await permissionsDS.createPermission(payload, this.props.presentation.id, false);
            let presentationId = this.props.presentation.id;
            incUserProps({
                invites: payload.emails.length
            });
            const props = {
                presentation_id: presentationId,
                old_value: null,
                new_value: payload.permission,
                recipient_emails: payload.emails,
                sender_email: app.user.getEmail(),
                location
            };
            trackActivity("Collab", "Invite", null, null, props, { audit: true });
            this.props.closeDialog();
        } catch (err) {
            log.error(err);
            ShowErrorDialog({
                error: "Failed to collaborate",
                message: err.message
            });
            this.setState({ isLoading: false });
        }
    };

    handleTeamInvite = async teamId => {
        this.setState({ isLoading: true });
        try {
            await permissionsDS.inviteTeam(teamId, "write", this.props.presentation.id, false);
        } catch (err) {
            log.error(err);
            ShowErrorDialog({
                error: "Failed to collaborated",
                message: err.message
            });
        } finally {
            this.setState({ isLoading: false });
        }
    }

    render() {
        let { expanded, presentation } = this.props;
        const {
            emails,
            customMessage,
            isInviteDisabled,
            permission,
            isLoading,
            showCollaboratorList
        } = this.state;

        return (
            <Fragment>
                {
                    !showCollaboratorList &&
                    <DialogPanel onClick={this.props.onSelected}>
                        <DialogTitle><Icon>person_add_alt_1</Icon>Invite collaborators</DialogTitle>
                        <Loadable isLoading={isLoading}>
                            <Expander style={{ maxHeight: expanded ? "500px" : "0px" }}>
                                <DialogContent>
                                    <FlexBox style={{ display: app.isConstrained ? "block" : "flex" }}>
                                        <InviteInputWithGContacts
                                            onInputChange={this.handleInputChange}
                                            tags={emails}
                                            showGoogleContacts={false}
                                        />
                                        <Gap10/>
                                        <Select
                                            variant="outlined"
                                            value={permission}
                                            onChange={event => this.handleDropdownChange(event.target.value)}
                                            style={app.isConstrained ? { width: "100%" } : null}
                                        >
                                            <MenuItem value="write">Can Edit</MenuItem>
                                            <MenuItem value="read">Can View</MenuItem>
                                        </Select>
                                    </FlexBox>

                                    <Gap10/>
                                    <TextArea
                                        isVisible
                                        text={customMessage}
                                        onChange={customMessage => this.handleChangeCustomMessage(customMessage) }
                                        placeholder="Add a note..."
                                    />
                                    {
                                        !app.isConstrained &&
                                        <Fragment>
                                            <Gap20/>
                                            {
                                                ds.teams.length > 0 &&
                                                <TeamGrid>
                                                    {ds.teams.models.map(team => {
                                                        if (team.get("orgId") === presentation.get("orgId")) {
                                                            return (
                                                                <BlueButton
                                                                    onClick={() => this.handleTeamInvite(team.get("id"))}
                                                                    key={team.get("id")}
                                                                    size="small"
                                                                >
                                                                    {team.get("isDefault") ? "All Team Members" : team.get("name")}
                                                                </BlueButton>
                                                            );
                                                        }
                                                        return null;
                                                    })}
                                                </TeamGrid>
                                            }
                                            {
                                                ds.teams.length == 0 &&
                                                <TeamCTA>
                                                    <span>Need to quickly collaborate with your entire team?</span>
                                                    <Gap10/>
                                                    <a onClick={this.handleTeamCTA}>Upgrade to the Team Plan</a>
                                                </TeamCTA>
                                            }
                                        </Fragment>
                                    }
                                </DialogContent>
                                {
                                    !app.isConstrained &&
                                    <DialogActions>
                                        {app.mainView.editorView && <Button onClick={this.handleViewAllCollaborators}>View all collaborators</Button>}
                                        <FlexSpacer/>
                                        <BlueButton disabled={isInviteDisabled} onClick={this.handleAccept} color="primary">
                                            Send Invite
                                        </BlueButton>
                                    </DialogActions>
                                }
                                {
                                    app.isConstrained &&
                                    <DialogActions
                                        style={{
                                            flexDirection: "column",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <BlueButton size="large" disabled={isInviteDisabled} onClick={this.handleAccept} color="primary">
                                            Send Invite
                                        </BlueButton>
                                        <Gap10/>
                                        {app.mainView.editorView && <Button
                                            className="text-blue"
                                            onClick={() => this.handleToggleCollaboratorList(true)}
                                        >View all collaborators</Button>}
                                    </DialogActions>
                                }
                            </Expander>
                        </Loadable>
                    </DialogPanel>
                }
                {
                    showCollaboratorList &&
                    <DialogPanel onClick={this.props.onSelected}>
                        <DialogTitle><Icon>person_add_alt_1</Icon>Who has access?</DialogTitle>
                        <Loadable isLoading={isLoading}>
                            <Expander style={{ maxHeight: expanded ? "500px" : "0px" }}>
                                <DialogContent>
                                    <PermissionsList
                                        orgId={presentation.get("orgId")}
                                        refreshOnMount={true}
                                    />
                                </DialogContent>
                                <DialogActions
                                    style={{
                                        flexDirection: "column",
                                        justifyContent: "center",
                                    }}
                                >
                                    <BlueButton
                                        size="large"
                                        onClick={() => this.handleToggleCollaboratorList(false)}
                                        color="primary"
                                    >Invite new collaborators</BlueButton>
                                </DialogActions>
                            </Expander>
                        </Loadable>
                    </DialogPanel>
                }
            </Fragment>
        );
    }
}

